import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { eventType } from "@/lib/constants";
import { X, Volume2, Popcorn, Sparkles, Mail, SkipForward } from "lucide-react";
import DisplayMusic from "@/components/DisplayMusic";
import { QRCodeCanvas } from "qrcode.react";

/**
 * Kino-Intro Vollbildsequenz — AUTO-START, echter Kino-Vorspann.
 * Reihenfolge: Logo → Slogan → Handy-Hinweis → [Trailer, Werbe-Slide, Trailer, ...] → Haupt-Trailer → Filmstart.
 * Trailer autoplay muted; erster User-Klick irgendwo entstummt und triggert Ambient-Cue.
 */
export default function KinoIntro() {
  const { showtimeId } = useParams();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]); // preroll items: {kind: 'trailer'|'ad_slide', ...}
  const [step, setStep] = useState(0);
  const [muted, setMuted] = useState(true);
  const [forceReveal, setForceReveal] = useState(false);
  const iframeRef = useRef(null);
  const audioPlayedRef = useRef(false);
  const lastCmdSeqRef = useRef(0);

  useEffect(() => {
    api.get(`/intro/${showtimeId}`).then((r) => {
      setData(r.data);
      const excl = r.data?.showtime?.movie?.tmdb_id;
      const count = Math.max(0, Math.min(10, Number(r.data?.showtime?.preroll_trailer_count ?? 5)));
      if (count === 0) {
        setItems([]);
        return;
      }
      api.get(`/trailers/preroll`, { params: { showtime_id: showtimeId, exclude_tmdb_id: excl, count } })
        .then((pr) => setItems(pr.data?.items || []))
        .catch(() => setItems([]));
    }).catch(() => {});
    document.documentElement.requestFullscreen?.().catch(() => {});
  }, [showtimeId]);

  const st = data?.showtime;
  const movie = st?.movie;
  const ev = eventType(st?.event_type);
  const ytId = getYoutubeId(movie?.trailer_url);

  // Reihenfolge im Kino (aktualisiert):
  //   1) Foyer (Musik + Logo, 5-7 min bis Filmstart)  →  2) Logo (Brand)  →  3) Slogan  →
  //   4) Handy-Hinweis  →  5) Werbung  →  6) Trailer/Videos  →  7) Haupt-Trailer  →  8) „Film startet"
  const adItems = useMemo(() => items.filter((i) => i.kind === "ad_slide"), [items]);
  const trailerItems = useMemo(() => items.filter((i) => i.kind === "trailer"), [items]);

  const foyerDurationMs = useMemo(() => {
    const min = Math.max(1, Math.min(20, Number(st?.foyer_duration_min || 6)));
    return min * 60 * 1000;
  }, [st?.foyer_duration_min]);

  const sequence = useMemo(() => {
    const seq = [];
    // Wandbild vor dem Foyer (optional, 0-10 min · pro Film wählbar)
    const wallMin = Math.max(0, Math.min(10, Number(st?.pre_foyer_wall_duration_min ?? 0)));
    const wallUrl = (st?.pre_foyer_wall_url || "").trim();
    if (wallMin > 0 && wallUrl) {
      seq.push({ kind: "wall", data: { url: wallUrl, minutes: wallMin } });
    }
    seq.push({ kind: "foyer" });
    seq.push({ kind: "brand" });
    seq.push({ kind: "slogan" });
    seq.push({ kind: "phone" });
    for (const ad of adItems) seq.push({ kind: "ad_slide", data: ad });
    for (const t of trailerItems) {
      seq.push({ kind: "trailer", data: t });
      seq.push({ kind: "poster", data: t });
    }
    seq.push({ kind: "start" });
    // Marathon-Filme (Fortsetzungen) — pro Zusatzfilm eine "Nächster Film"-Übergangsseite
    const marathon = st?.marathon_movies || [];
    for (const m of marathon) {
      seq.push({ kind: "marathon_next", data: m });
    }
    return seq;
  }, [adItems, trailerItems, st?.marathon_movies, st?.pre_foyer_wall_duration_min, st?.pre_foyer_wall_url]);

  // Trailer laufen bis zum echten Ende (YouTube-onStateChange). Als Safety-Fallback: 10 Min.
  const timings = useMemo(() => sequence.map((s) => {
    if (s.kind === "wall") return (s.data?.minutes || 0) * 60 * 1000;
    if (s.kind === "foyer") return foyerDurationMs;
    if (s.kind === "ad_slide") return (s.data?.duration_sec || 5) * 1000;
    if (s.kind === "brand") return 4200;
    if (s.kind === "slogan") return 3600;
    if (s.kind === "phone") return (st?.phone_image_url ? 8000 : 5400);
    if (s.kind === "trailer") return 600 * 1000;
    if (s.kind === "poster") return 3500;
    if (s.kind === "start") return 2600;
    if (s.kind === "marathon_next") return 12000;  // 12 s Übergangs-Karte
    return 3000;
  }), [sequence, foyerDurationMs, st?.phone_image_url]);

  useEffect(() => {
    if (step >= timings.length) return;
    const t = setTimeout(() => setStep((s) => s + 1), timings[step]);
    return () => clearTimeout(t);
  }, [step, timings]);

  const playCue = () => {
    if (audioPlayedRef.current) return;
    audioPlayedRef.current = true;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const master = ctx.createGain();
      master.gain.setValueAtTime(0, ctx.currentTime);
      master.gain.linearRampToValueAtTime(0.28, ctx.currentTime + 0.7);
      master.gain.linearRampToValueAtTime(0.0, ctx.currentTime + 3.6);
      master.connect(ctx.destination);
      [110, 220, 277.18, 329.63, 440].forEach((f, i) => {
        const o = ctx.createOscillator();
        o.type = i === 0 ? "sine" : (i < 3 ? "triangle" : "sine");
        o.frequency.setValueAtTime(f, ctx.currentTime);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0.35 / (i + 1), ctx.currentTime + 0.8 + i * 0.15);
        g.gain.linearRampToValueAtTime(0.0, ctx.currentTime + 3.4);
        o.connect(g); g.connect(master);
        o.start(); o.stop(ctx.currentTime + 3.8);
      });
    } catch { /* ignore */ }
  };
  useEffect(() => { playCue(); }, []);

  const unmute = useCallback(() => {
    if (!muted) return;
    setMuted(false);
    playCue();
    try {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "unMute", args: "" }),
        "*"
      );
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "setVolume", args: [100] }),
        "*"
      );
    } catch { /* ignore */ }
  }, [muted]);

  // First user gesture (anywhere) — auto-unmute
  useEffect(() => {
    if (!muted) return;
    const handler = () => unmute();
    window.addEventListener("click", handler, { once: true });
    window.addEventListener("keydown", handler, { once: true });
    window.addEventListener("touchstart", handler, { once: true });
    return () => {
      window.removeEventListener("click", handler);
      window.removeEventListener("keydown", handler);
      window.removeEventListener("touchstart", handler);
    };
  }, [muted, unmute]);

  // YouTube-Iframe-Events: infoDelivery liefert alle ~1s currentTime + duration.
  // Wir schalten 2.5s VOR dem Ende weiter, damit die YouTube-End-Screen (Watchlist, verwandte Videos,
  // Kanallogo etc.) gar nicht erst sichtbar wird — echter Kino-Look.
  useEffect(() => {
    let advancedForVideo = false;
    let lastVideo = null;
    function onMsg(e) {
      if (typeof e.data !== "string") return;
      try {
        const d = JSON.parse(e.data);
        // Beim onReady/onStateChange (play=1) merken wir uns die aktuelle Video-ID und resetten den Advance-Guard
        if (d.event === "onStateChange") {
          if (d.info === 1) { advancedForVideo = false; }
          if (d.info === 0 && !advancedForVideo) {
            advancedForVideo = true;
            setStep((s) => s + 1);
          }
        }
        if (d.event === "infoDelivery" && d.info) {
          const { currentTime, duration, videoData } = d.info;
          const vid = videoData?.video_id;
          if (vid && vid !== lastVideo) { lastVideo = vid; advancedForVideo = false; }
          if (
            typeof currentTime === "number" &&
            typeof duration === "number" &&
            duration > 5 &&
            currentTime >= duration - 2.5 &&
            !advancedForVideo
          ) {
            advancedForVideo = true;
            setStep((s) => s + 1);
          }
        }
      } catch { /* ignore */ }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Debug-Shortcut: ?jump=video springt direkt zum Film-Player (nur für Preview/Tests)
  const jumpToEnd = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("jump");
      return p === "video" || p === "end";
    } catch { return false; }
  }, []);

  // Polling der Operator-Fernsteuerung (Handy per QR sendet Kommandos)
  useEffect(() => {
    if (!showtimeId) return;
    const poll = async () => {
      try {
        const { data: st } = await api.get(`/intro-remote/${showtimeId}/state`);
        if (!st || !st.cmd) return;
        if (st.seq <= lastCmdSeqRef.current) return;
        lastCmdSeqRef.current = st.seq;
        // Kommando anwenden
        if (st.cmd === "skip") setStep((s) => s + 1);
        else if (st.cmd === "restart") { setStep(0); setForceReveal(false); }
        else if (st.cmd === "blackout") { setStep(sequence.length); setForceReveal(true); }
        else if (st.cmd === "reveal") setForceReveal((r) => !r);
      } catch { /* silent */ }
    };
    const t = setInterval(poll, 1500);
    poll();
    return () => clearInterval(t);
  }, [showtimeId, sequence.length]);

  const current = sequence[step];

  // Musik-URL für die aktuelle Phase (Wall → wall_music_url mit Foyer-Fallback, sonst Foyer-Musik)
  const currentMusicUrl = useMemo(() => {
    if (!current) return "";
    if (current.kind === "wall") {
      return (st?.wall_music_url || st?.foyer_music_url || "").trim();
    }
    if (["foyer", "brand", "slogan", "phone", "start", "ad_slide", "poster", "marathon_next"].includes(current.kind)) {
      return (st?.foyer_music_url || "").trim();
    }
    return "";  // Trailer + Film-Player laufen mit eigenem Audio
  }, [current, st?.wall_music_url, st?.foyer_music_url]);

  if (step >= sequence.length || jumpToEnd) {
    // After the intro sequence, honor the showtime's film source
    const kind = st?.film_source_kind || "appletv";
    const url = (st?.film_source_url || "").trim();
    const curtainSec = Math.max(0, Number(st?.curtain_open_seconds ?? 2.8));
    if (kind === "video" && url) {
      return <VideoPlayer src={url} title={movie?.title_de || "Film"} curtainSec={curtainSec} />;
    }
    if (kind === "stream" && url) {
      const yt = getYoutubeId(url);
      if (yt) return <StreamPlayer ytId={yt} title={movie?.title_de || "Film"} curtainSec={curtainSec} />;
      return <StreamPlayer iframeSrc={url} title={movie?.title_de || "Film"} curtainSec={curtainSec} />;
    }
    return <FinalCurtain movie={movie} forceReveal={forceReveal} />;
  }

  if (current?.kind === "foyer") {
    return (
      <>
        {currentMusicUrl && <DisplayMusic key={currentMusicUrl} url={currentMusicUrl} autoStart hideToggle />}
        <FoyerStep st={st} showtimeId={showtimeId} onSkip={() => setStep((s) => s + 1)} totalMs={foyerDurationMs} />
      </>
    );
  }

  if (current?.kind === "wall") {
    return (
      <>
        {currentMusicUrl && <DisplayMusic key={currentMusicUrl} url={currentMusicUrl} autoStart hideToggle />}
        <WallDisplayStep url={current.data.url} minutes={current.data.minutes} onSkip={() => setStep((s) => s + 1)} />
      </>
    );
  }

  if (current?.kind === "ad_slide") {
    const item = current.data;
    const adIdx = adItems.indexOf(item) + 1;
    return <AdSlideStep item={item} counter={`${adIdx} / ${adItems.length}`} onSkip={() => setStep((s) => s + 1)} />;
  }

  if (current?.kind === "trailer") {
    const item = current.data;
    const tIdx = trailerItems.indexOf(item) + 1;
    return (
      <TrailerStep
        ytId={item.trailer_youtube_id}
        title={`Vorspann · ${item.title}`}
        counter={`${tIdx} / ${trailerItems.length}`}
        muted={muted}
        onUnmute={unmute}
        onSkip={() => setStep((s) => s + 1)}
        iframeRef={iframeRef}
      />
    );
  }

  if (current?.kind === "poster") {
    return <PosterStep item={current.data} onSkip={() => setStep((s) => s + 1)} />;
  }

  if (current?.kind === "marathon_next") {
    return <MarathonNextStep movie={current.data} onSkip={() => setStep((s) => s + 1)} />;
  }

  return (
    <div className="fixed inset-0 bg-black overflow-hidden text-[#F5F0E6]" onClick={playCue}>
      <BackdropLayer movie={movie} />
      <div className="absolute inset-0 flex flex-col items-center justify-center p-10 text-center">
        {current?.kind === "brand" && <BrandStep />}
        {current?.kind === "slogan" && <SloganStep />}
        {current?.kind === "phone" && <PhoneStep imageUrl={st?.phone_image_url} />}
        {current?.kind === "start" && <StartStep movie={movie} ev={ev} />}
      </div>
      <div className="absolute bottom-4 right-6 text-[10px] uppercase tracking-[0.4em] text-[#575148]">
        Dolby Atmos · THE MOVIE HOUSE
      </div>
    </div>
  );
}

function FoyerStep({ st, showtimeId, onSkip, totalMs }) {
  const [remaining, setRemaining] = useState(totalMs);
  useEffect(() => {
    const started = Date.now();
    const i = setInterval(() => {
      const left = Math.max(0, totalMs - (Date.now() - started));
      setRemaining(left);
      if (left <= 0) clearInterval(i);
    }, 250);
    return () => clearInterval(i);
  }, [totalMs]);

  const min = Math.floor(remaining / 60000);
  const sec = Math.floor((remaining % 60000) / 1000).toString().padStart(2, "0");
  const welcome = (st?.welcome_text || "").trim();
  const movie = st?.movie;

  return (
    <div className="fixed inset-0 bg-black text-[#F5F0E6] overflow-hidden" data-testid="intro-foyer">
      {/* Musik wird jetzt auf Parent-Ebene gerendert (kein remount beim Wechsel Wall→Foyer, wenn URL gleich). */}

      {/* Backdrop */}
      {movie?.backdrop_url && (
        <>
          <img src={movie.backdrop_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover opacity-25 animate-slow-drift" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/70 to-black" />
        </>
      )}

      {/* Samt-Grain */}
      <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
        style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "3px 3px" }} />

      {/* Marquee-Bulbs oben/unten */}
      <div className="absolute inset-x-0 top-4 flex justify-between px-8 pointer-events-none">
        {Array.from({ length: 24 }).map((_, i) => (
          <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#D4AF37] shadow-[0_0_12px_rgba(212,175,55,0.9)] animate-bulb-slow" style={{ animationDelay: `${(i % 4) * 300}ms` }} />
        ))}
      </div>
      <div className="absolute inset-x-0 bottom-4 flex justify-between px-8 pointer-events-none">
        {Array.from({ length: 24 }).map((_, i) => (
          <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#D4AF37] shadow-[0_0_12px_rgba(212,175,55,0.9)] animate-bulb-slow" style={{ animationDelay: `${((i + 2) % 4) * 300}ms` }} />
        ))}
      </div>

      {/* Skip oben rechts — unsichtbar (nur für Operator per Hover erreichbar), damit Publikum kein Menü sieht */}
      <button data-testid="intro-foyer-skip" onClick={onSkip}
        aria-label="Vorspann starten"
        tabIndex={-1}
        className="absolute top-8 right-10 z-30 flex items-center gap-2 text-xs uppercase tracking-[0.32em] text-[#F5F0E6]/70 hover:text-[#D4AF37] bg-black/40 backdrop-blur px-4 py-2 rounded-full border border-white/10 opacity-0 hover:opacity-40 focus:opacity-70 transitions-safe">
        Vorspann starten <SkipForward size={14} />
      </button>

      {/* Zentrum */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-10 text-center">
        <div className="font-heading text-6xl md:text-9xl text-[#D4AF37] tracking-wide animate-slow-pulse" style={{ textShadow: "0 0 60px rgba(212,175,55,0.4)" }}>
          THE MOVIE HOUSE
        </div>
        <div className="mt-4 text-xs md:text-sm uppercase tracking-[0.5em] text-[#A8A398]">More Than Watching Movies.</div>
        <div className="mt-14 mx-auto h-px w-96 bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" />

        {welcome ? (
          <div className="mt-10 italic text-[#EEDC82] text-lg md:text-2xl max-w-3xl">&bdquo;{welcome}&ldquo;</div>
        ) : (
          <div className="mt-10 text-[#A8A398] text-sm md:text-base max-w-2xl">
            Machen Sie es sich bequem. Der Film beginnt gleich.
          </div>
        )}

        {/* Countdown zum Vorspann */}
        <div className="mt-16">
          <div className="text-[10px] uppercase tracking-[0.5em] text-[#A8A398]">Vorspann in</div>
          <div className="font-mono text-6xl md:text-8xl text-[#D4AF37] tabular-nums mt-2" data-testid="intro-foyer-countdown">
            {min}:{sec}
          </div>
        </div>

        {/* Film-Info */}
        {movie && (
          <div className="mt-16 flex items-center gap-5 opacity-90">
            {movie.poster_url && (
              <img src={movie.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-24 md:w-32 aspect-[2/3] object-cover rounded shadow-2xl" />
            )}
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Heute</div>
              <div className="font-heading text-2xl md:text-3xl mt-1">{movie.title_de || movie.title_original}</div>
              <div className="text-xs text-[#A8A398] mt-1">
                {st?.start_time && `Start ${st.start_time}`}{st?.hall && ` · ${st.hall}`}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-8 left-8 text-[10px] uppercase tracking-[0.4em] text-[#575148]">Foyer · Einlass läuft</div>
      <div className="absolute bottom-8 right-8 text-[10px] uppercase tracking-[0.4em] text-[#575148]">Dolby Atmos</div>

      <style>{`
        @keyframes slowDrift { 0%,100% { transform: scale(1.02) translateX(0) } 50% { transform: scale(1.05) translateX(-1%) } }
        .animate-slow-drift { animation: slowDrift 40s ease-in-out infinite; }
        @keyframes bulbSlow { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        .animate-bulb-slow { animation: bulbSlow 2.2s ease-in-out infinite; }
        @keyframes slowPulse { 0%,100% { transform: scale(1); text-shadow: 0 0 60px rgba(212,175,55,0.4); } 50% { transform: scale(1.02); text-shadow: 0 0 100px rgba(212,175,55,0.7); } }
        .animate-slow-pulse { animation: slowPulse 6s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

function PosterStep({ item, onSkip }) {
  const fsk = (item?.age_rating || "").trim();
  const genres = (item?.genre || "").split(/[,·]/).map((g) => g.trim()).filter(Boolean).slice(0, 4);
  return (
    <div className="fixed inset-0 bg-black text-[#F5F0E6] overflow-hidden" data-testid="intro-poster" onClick={onSkip}>
      {/* Backdrop */}
      {item?.poster_url && (
        <>
          <img src={item.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover opacity-25 blur-xl" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black" />
        </>
      )}
      <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
        style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "3px 3px" }} />

      <div className="relative z-10 min-h-screen flex items-center justify-center px-8 md:px-14 py-10 gap-10 md:gap-16 animate-in fade-in zoom-in-95 duration-700">
        {/* Poster */}
        {item?.poster_url ? (
          <img src={item.poster_url} alt={item.title}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="w-72 md:w-[420px] aspect-[2/3] object-cover rounded-lg shadow-[0_0_80px_rgba(212,175,55,0.35)] border-2 border-[#D4AF37]/40" />
        ) : (
          <div className="w-72 md:w-[420px] aspect-[2/3] bg-[#1A1A1A] rounded-lg flex items-center justify-center text-[#575148]">Kein Poster</div>
        )}
        {/* Info */}
        <div className="max-w-xl">
          <div className="text-xs uppercase tracking-[0.5em] text-[#D4AF37]">Demnächst</div>
          <div className="font-heading text-5xl md:text-7xl mt-4 leading-[0.95] break-words">{item?.title || "—"}</div>

          {/* FSK + Genre Chips */}
          {(fsk || genres.length > 0 || item?.duration_min > 0) && (
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {fsk && (
                <span className="text-sm md:text-base font-mono border-2 border-[#D4AF37] text-[#D4AF37] px-3 py-1 rounded uppercase" data-testid="poster-fsk">
                  {/^\d+$/.test(fsk) ? `FSK ${fsk}` : fsk}
                </span>
              )}
              {item?.duration_min > 0 && (
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] border border-[#2A2A2A] px-3 py-1.5 rounded-full">
                  {item.duration_min} Min
                </span>
              )}
              {genres.map((g, i) => (
                <span key={i} className="text-[10px] uppercase tracking-[0.32em] text-[#EEDC82] bg-[#D4AF37]/10 border border-[#D4AF37]/50 px-3 py-1.5 rounded-full" data-testid={`poster-genre-${i}`}>
                  {g}
                </span>
              ))}
            </div>
          )}

          <div className="mt-10 mx-0 h-px w-40 bg-gradient-to-r from-[#D4AF37] via-[#D4AF37]/40 to-transparent" />
          <div className="mt-6 italic text-[#EEDC82]/80 text-lg">Ein Erlebnis im Movie House.</div>
        </div>
      </div>

      <div className="absolute bottom-6 left-6 text-[10px] uppercase tracking-[0.4em] text-[#575148]">THE MOVIE HOUSE · Vorschau</div>
    </div>
  );
}


function BrandStep() {  return (
    <div className="animate-in fade-in zoom-in duration-1000">
      <div className="font-heading text-6xl md:text-9xl text-[#D4AF37] tracking-wide" style={{ textShadow: "0 0 40px rgba(212,175,55,0.35)" }}>
        THE MOVIE HOUSE
      </div>
      <div className="mt-6 h-px w-64 mx-auto bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
    </div>
  );
}
function SloganStep() {
  return (
    <div className="animate-in fade-in slide-in-from-bottom-6 duration-1000">
      <div className="font-heading italic text-4xl md:text-6xl text-[#F5F0E6]">More Than Watching Movies.</div>
      <div className="mt-6 text-xs tracking-[0.4em] uppercase text-[#A8A398]">Dolby · Atmos · Sound</div>
    </div>
  );
}
function PhoneStep({ imageUrl }) {
  if (imageUrl) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center animate-in fade-in duration-700" data-testid="intro-phone-image">
        <img src={imageUrl} alt="" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </div>
    );
  }
  return (
    <div className="animate-in fade-in duration-700 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37]">Ein Hinweis</div>
      <div className="font-heading text-3xl md:text-5xl mt-6 leading-tight">
        Bitte schalten Sie Ihre Mobiltelefone lautlos und genießen Sie den Film.
      </div>
      <div className="mt-8 text-[10px] tracking-[0.32em] uppercase text-[#A8A398]">Danke.</div>
    </div>
  );
}

function AdSlideStep({ item, counter, onSkip }) {
  const isMedia = !!item.media_url;
  const icon = adIconFor(item.title);
  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-hidden" style={{ background: item.background_color || "#0A0A0A", color: "#F5F0E6" }} data-testid="intro-ad-slide">
      {isMedia && (
        <img src={item.media_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover opacity-40 animate-adzoom" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="relative z-10 text-center px-10 max-w-4xl animate-in fade-in zoom-in-95 duration-700">
        <div className="text-xs uppercase tracking-[0.5em] mb-6" style={{ color: item.accent_color || "#D4AF37" }}>· Werbung ·</div>
        <div className="flex items-center justify-center gap-4 mb-6" style={{ color: item.accent_color || "#D4AF37" }}>
          {icon}
        </div>
        <div className="font-heading text-5xl md:text-8xl leading-[0.95]" style={{ color: item.accent_color || "#D4AF37", textShadow: "0 0 40px rgba(212,175,55,0.35)" }}>
          {item.title}
        </div>
        {item.subtitle && <div className="mt-6 text-lg md:text-2xl text-[#F5F0E6]/85 max-w-2xl mx-auto leading-relaxed">{item.subtitle}</div>}
        {item.cta && (
          <div className="mt-10 inline-block border-2 rounded-full px-8 py-3 text-sm uppercase tracking-[0.4em]" style={{ borderColor: item.accent_color || "#D4AF37", color: item.accent_color || "#D4AF37" }}>
            {item.cta}
          </div>
        )}
      </div>
      <div className="absolute top-6 left-6 text-[10px] uppercase tracking-[0.4em] text-[#F5F0E6]/70 bg-black/40 backdrop-blur px-3 py-2 rounded-full border border-white/10">
        Vorspann {counter && <span className="ml-2" style={{ color: item.accent_color || "#D4AF37" }}>{counter}</span>}
      </div>
      <button
        data-testid="intro-ad-skip"
        onClick={onSkip}
        className="absolute top-6 right-6 opacity-0 hover:opacity-30 focus:opacity-70 text-[#F5F0E6]/70 hover:text-[#D4AF37] flex items-center gap-2 text-xs uppercase tracking-[0.32em] bg-black/40 backdrop-blur px-3 py-2 rounded-full border border-white/10 transitions-safe"
        aria-label="Überspringen"
        tabIndex={-1}
      >
        Überspringen <X size={14} />
      </button>
      <div className="absolute bottom-6 left-6 text-[10px] uppercase tracking-[0.4em] text-[#575148]">THE MOVIE HOUSE · Vorspann</div>
      <style>{`@keyframes adzoom { 0% { transform: scale(1); } 100% { transform: scale(1.08); } } .animate-adzoom { animation: adzoom 5s linear both; }`}</style>
    </div>
  );
}

function MarathonNextStep({ movie, onSkip }) {
  return (
    <div className="fixed inset-0 bg-black text-[#F5F0E6] flex items-center justify-center overflow-hidden" onDoubleClick={onSkip} data-testid="intro-marathon-next">
      {movie?.backdrop_url && (
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${movie.backdrop_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/90" />
      <div className="relative z-10 flex items-center gap-10 px-10 animate-in fade-in slide-in-from-bottom-8 duration-1000">
        {movie?.poster_url && (
          <img src={movie.poster_url} alt=""
            className="w-64 h-auto rounded-lg shadow-2xl shadow-black/60 border border-[#D4AF37]/30"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
        <div className="max-w-xl text-left">
          <div className="text-[11px] uppercase tracking-[0.5em] text-[#D4AF37]">Marathon-Abend · Nächster Film</div>
          <div className="font-heading text-5xl md:text-7xl mt-4 leading-tight">{movie?.title_de || movie?.title_original || "Nächster Film"}</div>
          {movie?.duration_min && (
            <div className="mt-3 text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">Dauer · {movie.duration_min} Min</div>
          )}
          <div className="mt-10 flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-[#D4AF37] animate-pulse" />
            <div className="text-[10px] uppercase tracking-[0.5em] text-[#F5F0E6]/60">Bitte einen Moment</div>
          </div>
        </div>
      </div>
    </div>
  );
}


function adIconFor(title = "") {
  const t = title.toLowerCase();
  if (t.includes("snack") || t.includes("popcorn")) return <Popcorn size={64} />;
  if (t.includes("club") || t.includes("mitglied")) return <Sparkles size={64} />;
  if (t.includes("newsletter") || t.includes("mail")) return <Mail size={64} />;
  return <Sparkles size={64} />;
}

function TrailerStep({ ytId, onSkip, muted, iframeRef }) {
  // youtube-nocookie.com verhindert die meisten Personalisierungs-Elemente (Captions, Kanal-Branding)
  // Zusätzlich alle bekannten Parameter zum Unterdrücken von UI-Elementen:
  //   - controls=0, modestbranding=1, rel=0, iv_load_policy=3 → keine Anmerkungen/verwandte Videos
  //   - cc_load_policy=0, cc_lang_pref=none, hl=de → Untertitel/Captions aus
  //   - disablekb=1, showinfo=0, playsinline=1 → keine Tastatursteuerung, keine Titel-Overlays
  //   - annotations=0 → alte Anmerkungen aus
  const src = `https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&mute=${muted ? 1 : 0}&controls=0&modestbranding=1&rel=0&fs=0&iv_load_policy=3&playsinline=1&enablejsapi=1&cc_load_policy=0&cc_lang_pref=none&hl=de&disablekb=1&showinfo=0&annotations=0&origin=${encodeURIComponent(window.location.origin)}`;
  const handleLoad = () => {
    try {
      const win = iframeRef?.current?.contentWindow;
      // Handshake registrieren, damit onReady/onStateChange durchkommen
      win?.postMessage(JSON.stringify({ event: "listening", id: "trailer", channel: "widget" }), "*");
      // Captions AGGRESSIV ausschalten (mehrere Wege, denn YouTube ignoriert cc_load_policy oft)
      const killCaptions = () => {
        try {
          win?.postMessage(JSON.stringify({ event: "command", func: "unloadModule", args: ["captions"] }), "*");
          win?.postMessage(JSON.stringify({ event: "command", func: "unloadModule", args: ["cc"] }), "*");
          win?.postMessage(JSON.stringify({ event: "command", func: "setOption", args: ["captions", "track", {}] }), "*");
          win?.postMessage(JSON.stringify({ event: "command", func: "setOption", args: ["cc", "track", {}] }), "*");
        } catch { /* ignore */ }
      };
      killCaptions();
      // Nochmals nach 800ms und 2s, falls YouTube die Captions nach Ready wieder anschaltet
      setTimeout(killCaptions, 800);
      setTimeout(killCaptions, 2000);
      setTimeout(killCaptions, 4000);
    } catch { /* ignore */ }
  };
  return (
    <div className="fixed inset-0 bg-black" data-testid="intro-trailer" onDoubleClick={onSkip}>
      <iframe
        ref={iframeRef}
        title="trailer"
        className="w-full h-full"
        src={src}
        onLoad={handleLoad}
        allow="autoplay; encrypted-media"
      />
      {/* Overlay unten links versteckt Kanal-Branding (Kinocheck-Logo o. ä.) */}
      <div className="absolute bottom-0 left-0 w-56 h-24 bg-black pointer-events-none" aria-hidden />
      {/* Overlay oben rechts versteckt „Watch on YouTube"-Button */}
      <div className="absolute top-0 right-0 w-56 h-24 bg-black pointer-events-none" aria-hidden />
    </div>
  );
}
function StartStep({ movie, ev }) {
  return (
    <div className="animate-in fade-in zoom-in duration-1000">
      {ev && ev.v !== "regular" && (
        <div className="mb-8 text-xs uppercase tracking-[0.4em]" style={{ color: ev.color }}>
          {ev.icon} {ev.l}
        </div>
      )}
      <div className="font-heading text-5xl md:text-8xl text-[#F5F0E6] mt-4">Es startet: {movie?.title_de || "Ihr Film"}</div>
    </div>
  );
}
function FinalCurtain({ movie }) {
  // Kino-Vorhang für externe Quellen (Apple TV / Beamer HDMI).
  // Zeigt reines Schwarz + sehr dezenten Logo-Hauch, damit der Operator im Hintergrund
  // die Apple-TV-App öffnen kann OHNE dass die Zuschauer das GUI sehen.
  // Erst per Tastendruck (Leertaste / Enter / F) wird der Browser komplett ausgeblendet,
  // sodass die HDMI-Quelle (Apple TV) sichtbar wird.
  const [reveal, setReveal] = React.useState(false); // true = komplett schwarz, Logo weg
  const [hint, setHint] = React.useState(true);

  React.useEffect(() => {
    // Hinweis nach 8s ausblenden, damit der Zuschauer nichts liest
    const t = setTimeout(() => setHint(false), 8000);
    return () => clearTimeout(t);
  }, []);

  React.useEffect(() => {
    const onKey = async (e) => {
      // Nur Leertaste / Enter / F / Escape triggern die Übergabe — kein Klick, damit
      // versehentliche Maus-Bewegungen den Vorhang nicht öffnen.
      const keys = ["Space", "Enter", "KeyF", "Escape"];
      if (!keys.includes(e.code)) return;
      setReveal(true);
      // Bewusst KEIN exitFullscreen und KEIN window.close() — der Browser bleibt
      // schwarz und fullscreen, damit die Projektion niemals kurz das Desktop zeigt.
      // Die Umschaltung zur Apple-TV-Quelle passiert extern (HDMI-Switch, zweites
      // Display, oder AirPlay-Wechsel).
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black text-[#F5F0E6] flex items-center justify-center cursor-none select-none" data-testid="intro-final">
      {!reveal && (
        <div className="text-center opacity-[0.18] transition-opacity duration-700">
          <div className="font-heading text-4xl md:text-6xl tracking-[0.15em]">THE MOVIE HOUSE</div>
          <div className="text-[10px] uppercase tracking-[0.5em] mt-3">More Than Watching Movies</div>
        </div>
      )}
      {/* Diskreter Operator-Hinweis unten – blendet nach 8s aus, damit Publikum nichts sieht */}
      {!reveal && hint && (
        <div
          className="absolute bottom-6 right-6 text-[9px] uppercase tracking-[0.4em] text-[#F5F0E6]/25"
          data-testid="intro-final-hint"
        >
          [Taste] Übergabe an Apple TV · {movie?.title_de || "Film"}
        </div>
      )}
      {/* Nach Tastendruck: komplett schwarz, nichts mehr sichtbar */}
      {reveal && <div className="absolute inset-0 bg-black" />}
    </div>
  );
}

function WallDisplayStep({ url, minutes, onSkip }) {
  const totalMs = Math.max(1, Number(minutes) || 1) * 60 * 1000;
  const [remaining, setRemaining] = React.useState(totalMs);
  React.useEffect(() => {
    const started = Date.now();
    const i = setInterval(() => {
      const left = Math.max(0, totalMs - (Date.now() - started));
      setRemaining(left);
      if (left <= 0) { clearInterval(i); onSkip?.(); }
    }, 500);
    return () => clearInterval(i);
  }, [totalMs, onSkip]);
  const min = Math.floor(remaining / 60000);
  const sec = Math.floor((remaining % 60000) / 1000).toString().padStart(2, "0");
  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center" data-testid="intro-wall-display">
      <img src={url} alt="" className="w-full h-full object-contain" />
      {/* Diskreter Skip-Button oben rechts (nur für Operator) */}
      <button data-testid="intro-wall-skip" onClick={onSkip} aria-label="Foyer starten" tabIndex={-1}
        className="absolute top-8 right-10 z-30 text-[10px] uppercase tracking-[0.4em] text-[#F5F0E6]/60 bg-black/40 backdrop-blur px-3 py-2 rounded-full border border-white/10 opacity-0 hover:opacity-60 focus:opacity-80 transitions-safe">
        Weiter · {min}:{sec}
      </button>
    </div>
  );
}


function VideoPlayer({ src, title, curtainSec = 2.8 }) {
  const ref = React.useRef(null);
  const [curtainOpen, setCurtainOpen] = React.useState(false);
  const [playing, setPlaying] = React.useState(false);
  const [playError, setPlayError] = React.useState(false);

  React.useEffect(() => {
    // Kurze Verzögerung, dann Vorhang öffnen
    const openDelay = 800;
    const animMs = Math.max(0, curtainSec * 1000);
    const t1 = setTimeout(() => setCurtainOpen(true), openDelay);
    // Nach Öffnungs-Animation Film starten (Fullscreen + Play)
    const t2 = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      el.muted = false;
      el.play?.().catch(() => { el.muted = true; el.play?.().catch(() => {}); });
      el.requestFullscreen?.().catch(() => {});
      setPlaying(true);
    }, openDelay + animMs);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [curtainSec]);

  return (
    <div className="fixed inset-0 bg-black overflow-hidden" data-testid="intro-video-player">
      <video ref={ref} className="w-full h-full" controls={playing} playsInline preload="auto"
        onError={() => setPlayError(true)}>
        {/* HEVC-Hint zuerst: Safari + Chrome/Edge mit HW-Decoder erkennen es */}
        <source src={src} type='video/mp4; codecs="hvc1"' />
        {/* Fallback: generisches MP4 (H.264) */}
        <source src={src} type="video/mp4" />
        <source src={src} />
      </video>
      <StageCurtain open={curtainOpen} title={title} durationMs={curtainSec * 1000} />
      {playing && (
        <div className="absolute top-6 left-6 text-[10px] uppercase tracking-[0.4em] text-[#F5F0E6]/70 bg-black/40 backdrop-blur px-3 py-2 rounded-full border border-white/10" data-testid="intro-video-title">
          {title}
        </div>
      )}
      {playError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/95 z-50" data-testid="intro-video-error">
          <div className="max-w-lg text-center p-8 border border-[#7A1921]/50 rounded-lg bg-[#0A0A0A]">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#EE9CA1] mb-3">Wiedergabe fehlgeschlagen</div>
            <div className="font-heading text-xl text-[#F5F0E6]">Dieser Browser kann die Videodatei nicht direkt abspielen.</div>
            <div className="text-xs text-[#A8A398] mt-3">Wahrscheinlich HEVC/H.265 in Firefox. Bitte Safari, Chrome oder Edge nutzen — oder die Datei im Admin neu hochladen (wird automatisch nach H.264 konvertiert).</div>
          </div>
        </div>
      )}
    </div>
  );
}

function StageCurtain({ open, title, durationMs = 2800 }) {
  const dur = Math.max(0, Number(durationMs) || 0);
  return (
    <div className={`absolute inset-0 pointer-events-none z-40 ${open ? "curtain-open" : ""}`} data-testid="stage-curtain" style={{ "--curtain-dur": `${dur}ms` }}>
      {/* Linker Vorhang */}
      <div className="curtain-panel curtain-left">
        <div className="curtain-image curtain-image-left" />
        <div className="curtain-shade" />
        <div className="curtain-fringe" />
      </div>
      {/* Rechter Vorhang */}
      <div className="curtain-panel curtain-right">
        <div className="curtain-image curtain-image-right" />
        <div className="curtain-shade" />
        <div className="curtain-fringe" />
      </div>

      {/* Titelkarte in der Mitte, verschwindet mit dem Vorhang */}
      <div className={`absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-700 z-10 ${open ? "opacity-0" : "opacity-100"}`}>
        <div className="text-[10px] uppercase tracking-[0.6em] text-[#D4AF37] drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">Es beginnt</div>
        <div className="font-heading text-5xl md:text-7xl text-[#F5F0E6] mt-3" style={{ textShadow: "0 4px 20px rgba(0,0,0,1), 0 0 40px rgba(0,0,0,0.9), 0 0 80px rgba(212,175,55,0.4)" }}>
          {title}
        </div>
        <div className="mt-6 flex items-center gap-3 text-[#D4AF37] drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
          <div className="h-px w-16 bg-[#D4AF37]/70" />
          <div className="text-[9px] uppercase tracking-[0.5em]">The Movie House</div>
          <div className="h-px w-16 bg-[#D4AF37]/70" />
        </div>
      </div>

      <style>{`
        .curtain-panel {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 52%;
          overflow: hidden;
          transform-origin: top center;
          transition: transform var(--curtain-dur, 2800ms) cubic-bezier(.55, 0, .32, 1);
          will-change: transform;
          box-shadow: 0 0 60px rgba(0,0,0,0.6) inset;
        }
        .curtain-left  { left: 0; }
        .curtain-right { right: 0; }
        .curtain-open .curtain-left  { transform: translateX(-102%) skewY(2deg); }
        .curtain-open .curtain-right { transform: translateX( 102%) skewY(-2deg); }

        /* Original Login-Background: Unsplash-Foto eines roten Samt-Vorhangs.
           Beide Seiten teilen sich das Bild — links zeigt linke Bildhälfte, rechts die rechte. */
        .curtain-image {
          position: absolute; inset: 0;
          background-image: url("https://images.unsplash.com/photo-1705944601101-fdb49dbf884c?w=1920&q=80");
          background-size: 200% 100%;
          background-repeat: no-repeat;
          filter: brightness(0.72) saturate(1.15);
        }
        .curtain-image-left  { background-position: left center; }
        .curtain-image-right { background-position: right center; }

        /* Dunkler Verlauf drüber (wie Login: from-black/80 via-black/60 to-black),
           damit Text lesbar bleibt und die Falten-Tiefe stimmt. */
        .curtain-shade {
          position: absolute; inset: 0;
          background:
            linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.85) 100%),
            radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.5) 100%);
        }
        .curtain-left .curtain-shade  { box-shadow: inset -30px 0 60px rgba(0,0,0,0.6); }
        .curtain-right .curtain-shade { box-shadow: inset  30px 0 60px rgba(0,0,0,0.6); }

        /* Goldene Kordel am Vorhangsaum unten */
        .curtain-fringe {
          position: absolute; left: 0; right: 0; bottom: 0; height: 10px;
          background:
            radial-gradient(circle at 8px 0, rgba(212,175,55,0.9) 3px, transparent 4px) 0 0 / 16px 10px repeat-x;
          filter: drop-shadow(0 3px 4px rgba(0,0,0,0.6));
          opacity: 0.75;
        }
      `}</style>
    </div>
  );
}

function StreamPlayer({ ytId, iframeSrc, title, curtainSec = 2.8 }) {
  const src = ytId
    ? `https://www.youtube.com/embed/${ytId}?autoplay=1&controls=1&modestbranding=1&rel=0&fs=1&playsinline=1&enablejsapi=1`
    : iframeSrc;
  const [curtainOpen, setCurtainOpen] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setCurtainOpen(true), 800);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="fixed inset-0 bg-black overflow-hidden" data-testid="intro-stream-player">
      <iframe title="stream" className="w-full h-full" src={src} allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
      <StageCurtain open={curtainOpen} title={title} durationMs={curtainSec * 1000} />
      <div className="absolute top-6 left-6 text-[10px] uppercase tracking-[0.4em] text-[#F5F0E6]/70 bg-black/40 backdrop-blur px-3 py-2 rounded-full border border-white/10">
        {title}
      </div>
    </div>
  );
}
function BackdropLayer({ movie }) {
  if (!movie?.backdrop_url) {
    return <div className="absolute inset-0 bg-gradient-to-b from-[#3B0C10] via-black to-black opacity-70" />;
  }
  return (
    <>
      <img src={movie.backdrop_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black" />
    </>
  );
}
function getYoutubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    return u.searchParams.get("v");
  } catch { return null; }
}
